import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { authService } from "../../lib/api/auth";
import { useAuthStore } from "../../stores/authStore";
import { Logo } from "../../components/common/Logo";

type VerifyState = "verifying" | "success" | "error";

const MagicLinkVerifyPage = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [state, setState] = useState<VerifyState>("verifying");
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        if (!token) {
            setState("error");
            setErrorMessage(t("auth.magicLinkNoToken", "No magic link token found. Please click the link in your email again."));
            return;
        }

        const verify = async () => {
            try {
                const response = await authService.verifyMagicLink(token);

                if (response.success) {
                    const { user, tokens } = response.data;

                    // Store auth state
                    useAuthStore.getState().setTokens({
                        accessToken: tokens.accessToken,
                        refreshToken: tokens.refreshToken,
                        expiresIn: 3600,
                    });
                    useAuthStore.getState().setUser({
                        id: user.id,
                        email: user.email,
                        name: user.name,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        currency: user.currency,
                        timezone: user.timezone,
                        isActive: user.isActive,
                        country: user.country,
                        emailVerified: user.emailVerified ?? true,
                        emailVerifiedAt: user.emailVerifiedAt,
                        onboardingStatus: user.onboardingStatus,
                        createdAt: user.createdAt,
                    });

                    // Update the full auth state
                    useAuthStore.setState({ isAuthenticated: true });

                    setState("success");

                    // Redirect to boards after brief success display
                    setTimeout(() => {
                        navigate("/boards", { replace: true });
                    }, 1500);
                }
            } catch (err: unknown) {
                setState("error");
                const error = err as { response?: { data?: { error?: string; code?: string } } };
                const code = error?.response?.data?.code;

                if (code === "AUTH_MAGIC_LINK_EXPIRED") {
                    setErrorMessage(t("auth.magicLinkExpired", "This magic link has expired. Please request a new one."));
                } else if (code === "AUTH_INVALID_MAGIC_LINK") {
                    setErrorMessage(t("auth.magicLinkInvalid", "This magic link is invalid or has already been used."));
                } else {
                    setErrorMessage(t("auth.magicLinkVerifyError", "We couldn't verify your magic link. Please try again."));
                }
            }
        };

        verify();
    }, [token, navigate, t]);

    return (
        <div className="min-h-screen bg-background-page flex flex-col items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="flex justify-center mb-8">
                    <Link to="/" className="flex items-center gap-3">
                        <Logo className="w-8 h-8" />
                        <span className="text-2xl font-display font-semibold text-text-primary dark:text-neutral-100">Dashbal</span>
                    </Link>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800 p-8">
                    {state === "verifying" && (
                        <div className="text-center space-y-4">
                            <div className="mx-auto w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin" />
                            <h2 className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                {t("auth.magicLinkVerifying", "Signing you in...")}
                            </h2>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                {t("auth.magicLinkPleaseWait", "Please wait while we verify your magic link.")}
                            </p>
                        </div>
                    )}

                    {state === "success" && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center space-y-4"
                        >
                            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                {t("auth.magicLinkSuccess", "You're signed in!")}
                            </h2>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                {t("auth.magicLinkRedirecting", "Redirecting you to your dashboard...")}
                            </p>
                        </motion.div>
                    )}

                    {state === "error" && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center space-y-4"
                        >
                            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h2 className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                {t("auth.magicLinkFailed", "Sign-in failed")}
                            </h2>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                {errorMessage}
                            </p>
                            <div className="pt-2">
                                <Link
                                    to="/?modal=login"
                                    className="btn-primary inline-block px-6 py-2.5"
                                >
                                    {t("auth.backToSignIn")}
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default MagicLinkVerifyPage;
